import { useState } from "react";
import { Accordion, Button, Textarea } from "@marekl/adminer/components";
import styles from "./TemplateDetailPage.module.css";

interface SampleDataPanelProps {
  value: string;
  onChange: (value: string) => void;
  /** Set when the JSON is invalid; rendered as the field error. */
  error: string | null;
}

/**
 * Collapsible editor for `sample_data_json`. The text is persisted with the
 * draft (PUT /draft) and feeds the live preview and test sends. The
 * `Accordion.Item` body unmounts while closed — safe, the text state lives
 * above — so the collapsed summary carries the invalid-JSON signal instead
 * of an always-mounted inline error.
 */
export function SampleDataPanel({ value, onChange, error }: SampleDataPanelProps) {
  // Controlled so the Format action can render only while the body is open.
  const [open, setOpen] = useState(false);

  function format() {
    try {
      onChange(JSON.stringify(JSON.parse(value), null, 2));
    } catch {
      // invalid JSON — the inline error already says so
    }
  }

  return (
    <Accordion.Item
      title="Sample data"
      summary={
        error ? (
          <span className={styles.inlineError}>invalid JSON</span>
        ) : (
          "JSON for the preview and test email; saved with the draft"
        )
      }
      actions={
        open ? (
          <Button size="sm" weight="ghost" onClick={format} disabled={!!error}>
            Format
          </Button>
        ) : undefined
      }
      open={open}
      onOpenChange={setOpen}
    >
      <Textarea
        aria-label="Sample data (JSON)"
        rows={10}
        spellCheck={false}
        className={styles.jsonTextarea}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={error ?? undefined}
        hint='E.g. {"user": {"name": "Jane"}, "items": [{"title": "…"}]}'
      />
    </Accordion.Item>
  );
}
